import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ChevronRight, Gift, History, Megaphone, Receipt, Repeat, ShoppingBag } from "lucide-react";
import RecruitmentShell from "../components/RecruitmentShell";
import RecruitmentProfileSettingsNav from "../components/RecruitmentProfileSettingsNav";
import HiringBuyJobsModal, { HiringManagePlanModal } from "../components/HiringBuyJobsModal";
import {
  getHiringBillingSummary,
  getHiringBillingTransactions,
} from "../apiClient";
import { HIRING_LAUNCH_FREE_MODE } from "../lib/hiringLaunchMode";
import {
  billingTransactionCostLabel,
  billingTransactionCostIsZero,
  billingTransactionDescription,
  billingTransactionTitle,
  billingTransactionVisualKind,
  formatBillingOneOffPrice,
  purchasesAccountSummaryLines,
} from "../lib/hiringPurchasesDisplay";

function formatTransactionWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function historyRowIcon(kind) {
  if (kind === "bonus") {
    return <Gift className="nd1-recruitment-purchases-history-row__icon" strokeWidth={1.75} aria-hidden="true" />;
  }
  if (kind === "purchase") {
    return (
      <ShoppingBag className="nd1-recruitment-purchases-history-row__icon" strokeWidth={1.75} aria-hidden="true" />
    );
  }
  if (kind === "publish") {
    return (
      <Megaphone className="nd1-recruitment-purchases-history-row__icon" strokeWidth={1.75} aria-hidden="true" />
    );
  }
  if (kind === "subscription") {
    return <Repeat className="nd1-recruitment-purchases-history-row__icon" strokeWidth={1.75} aria-hidden="true" />;
  }
  return <Receipt className="nd1-recruitment-purchases-history-row__icon" strokeWidth={1.75} aria-hidden="true" />;
}

export default function RecruitmentPurchasesPage({ currentUser, setCurrentUser, authToken }) {
  const [billingSummary, setBillingSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [managePlanOpen, setManagePlanOpen] = useState(false);

  const loadPage = useCallback(async () => {
    const token = String(authToken || "").trim();
    if (!token) {
      setError("You need to be signed in to view purchases.");
      setBillingSummary(null);
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [summary, history] = await Promise.all([
        getHiringBillingSummary(token),
        getHiringBillingTransactions(token),
      ]);
      setBillingSummary(summary);
      setTransactions(Array.isArray(history.transactions) ? history.transactions : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your purchases right now.");
      setBillingSummary(null);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const isPayg = !billingSummary || billingSummary.billingType === "pay_as_you_go";
  const priceLabel = formatBillingOneOffPrice(billingSummary);
  const summaryLines = billingSummary ? purchasesAccountSummaryLines(billingSummary) : null;
  const prepaidJobSlots = Math.max(0, Math.floor(Number(billingSummary?.prepaidJobSlots) || 0));

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <main className="nd1-shell nd1-recruitment-profile-main jw-recruitment-main recruitment-profile-page recruitment-purchases-page">
        <div className="recruitment-profile-shell nd1-recruitment-profile-shell">
          <header className="recruitment-profile-masthead nd1-recruitment-profile-masthead">
            <Link className="back-link nd1-recruitment-profile-back" to="/hiring-dashboard">
              &larr; Back to recruitment dashboard
            </Link>
            <p className="eyebrow nd1-label nd1-recruitment-profile-eyebrow">Billing</p>
            <h1>Purchases</h1>
            <p className="subtitle">Job slots and your billing history.</p>
            <RecruitmentProfileSettingsNav active="purchases" />
          </header>

          <div className="recruitment-profile-content profile-manage-card nd1-recruitment-profile-content">
            {error ? (
              <p className="subtitle" role="alert">
                {error}
              </p>
            ) : null}

            {loading ? <p className="subtitle">Loading your billing details…</p> : null}

            {!loading && billingSummary ? (
              <div className="recruitment-profile-stack recruitment-profile-stack--settings nd1-recruitment-purchases-stack">
                <section
                  className="recruitment-profile-surface nd1-recruitment-purchases-summary"
                  aria-labelledby="purchases-summary-heading"
                >
                  <p
                    className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading"
                    id="purchases-summary-heading"
                  >
                    <CreditCard className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                    Your account
                  </p>
                  <div className="nd1-recruitment-purchases-summary__copy">
                    <p className="nd1-recruitment-purchases-summary__line">
                      {isPayg ? (
                        <>
                          Your account is currently a <strong>Pay as you go</strong> account.
                        </>
                      ) : (
                        summaryLines.typeLine
                      )}
                    </p>
                    <p className="nd1-recruitment-purchases-summary__line nd1-recruitment-purchases-summary__line--balance">
                      {isPayg ? (
                        <>
                          You have{" "}
                          <strong>
                            {prepaidJobSlots} job slot{prepaidJobSlots === 1 ? "" : "s"}
                          </strong>{" "}
                          available.
                        </>
                      ) : (
                        summaryLines.balanceLine
                      )}
                    </p>
                  </div>
                  {priceLabel && isPayg && !HIRING_LAUNCH_FREE_MODE ? (
                    <p className="nd1-recruitment-purchases-summary__price">
                      Job slots from <strong>{priceLabel}</strong> each
                    </p>
                  ) : null}
                  {HIRING_LAUNCH_FREE_MODE && isPayg ? (
                    <p className="nd1-recruitment-purchases-summary__price">
                      Launch mode — job slots are included with your account. Need more?{" "}
                      <a className="nd1-auth-link-inline" href="mailto:hello@jobuwin.com">
                        Email us at hello@jobuwin.com
                      </a>
                      .
                    </p>
                  ) : null}
                </section>

                {!HIRING_LAUNCH_FREE_MODE ? (
                <section
                  className="recruitment-profile-surface nd1-recruitment-purchases-actions"
                  aria-labelledby="purchases-actions-heading"
                >
                  <p
                    className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading nd1-recruitment-purchases-actions__heading"
                    id="purchases-actions-heading"
                  >
                    <Receipt className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                    {isPayg ? "Buy or upgrade" : "Manage subscription"}
                  </p>

                  <div className="nd1-recruitment-purchases-action-grid">
                    {isPayg ? (
                      <>
                        <button
                          type="button"
                          className="nd1-recruitment-purchases-action-card nd1-recruitment-purchases-action-card--primary"
                          onClick={() => setBuyModalOpen(true)}
                        >
                          <span className="nd1-recruitment-purchases-action-card__head">
                            <span className="nd1-recruitment-purchases-action-card__icon-wrap nd1-recruitment-purchases-action-card__icon-wrap--accent">
                              <ShoppingBag
                                className="nd1-recruitment-purchases-action-card__icon"
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                            </span>
                            <ChevronRight
                              className="nd1-recruitment-purchases-action-card__chevron"
                              size={18}
                              aria-hidden="true"
                            />
                          </span>
                          <span className="nd1-recruitment-purchases-action-card__body">
                            <span className="nd1-recruitment-purchases-action-card__title">Buy job slots</span>
                            <span className="nd1-recruitment-purchases-action-card__copy">
                              Pay as you go — slots don&apos;t expire.
                            </span>
                          </span>
                        </button>
                        <Link
                          to="/pricing"
                          className="nd1-recruitment-purchases-action-card nd1-recruitment-purchases-action-card--secondary"
                        >
                          <span className="nd1-recruitment-purchases-action-card__head">
                            <span className="nd1-recruitment-purchases-action-card__icon-wrap nd1-recruitment-purchases-action-card__icon-wrap--blue">
                              <Repeat
                                className="nd1-recruitment-purchases-action-card__icon"
                                strokeWidth={1.75}
                                aria-hidden="true"
                              />
                            </span>
                            <ChevronRight
                              className="nd1-recruitment-purchases-action-card__chevron"
                              size={18}
                              aria-hidden="true"
                            />
                          </span>
                          <span className="nd1-recruitment-purchases-action-card__body">
                            <span className="nd1-recruitment-purchases-action-card__title">Start a subscription</span>
                            <span className="nd1-recruitment-purchases-action-card__copy">
                              Save when you hire regularly each month.
                            </span>
                          </span>
                        </Link>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="nd1-recruitment-purchases-action-card nd1-recruitment-purchases-action-card--primary"
                        onClick={() => setManagePlanOpen(true)}
                      >
                        <span className="nd1-recruitment-purchases-action-card__head">
                          <span className="nd1-recruitment-purchases-action-card__icon-wrap nd1-recruitment-purchases-action-card__icon-wrap--blue">
                            <Repeat
                              className="nd1-recruitment-purchases-action-card__icon"
                              strokeWidth={1.75}
                              aria-hidden="true"
                            />
                          </span>
                          <ChevronRight
                            className="nd1-recruitment-purchases-action-card__chevron"
                            size={18}
                            aria-hidden="true"
                          />
                        </span>
                        <span className="nd1-recruitment-purchases-action-card__body">
                          <span className="nd1-recruitment-purchases-action-card__title">Change subscription</span>
                          <span className="nd1-recruitment-purchases-action-card__copy">
                            Switch plan or monthly job allowance.
                          </span>
                        </span>
                      </button>
                    )}
                  </div>
                </section>
                ) : isPayg ? (
                  <section
                    className="recruitment-profile-surface nd1-recruitment-purchases-actions"
                    aria-labelledby="purchases-launch-help-heading"
                  >
                    <p
                      className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading"
                      id="purchases-launch-help-heading"
                    >
                      Need more job slots?
                    </p>
                    <p className="nd1-recruitment-purchases-summary__line">
                      Email us at{" "}
                      <a href="mailto:hello@jobuwin.com">hello@jobuwin.com</a> and we&apos;ll sort you out.
                    </p>
                  </section>
                ) : null}

                <section
                  id="billing-history"
                  className="recruitment-profile-surface nd1-recruitment-purchases-history"
                  aria-labelledby="purchases-history-heading"
                >
                  <p
                    className="profile-manage-section-title recruitment-profile-surface-heading nd1-recruitment-profile-section-heading"
                    id="purchases-history-heading"
                  >
                    <History className="nd1-recruitment-profile-section-icon" strokeWidth={1.75} aria-hidden="true" />
                    Billing history
                  </p>

                  {transactions.length === 0 ? (
                    <p className="nd1-recruitment-purchases-empty">
                      Nothing here yet — when you buy slots or use them on a role, it&apos;ll show up here.
                    </p>
                  ) : (
                    <ul className="nd1-recruitment-purchases-history-list">
                      {transactions.map((tx) => {
                        const visualKind = billingTransactionVisualKind(tx);
                        const costLabel = billingTransactionCostLabel(tx);
                        const description = billingTransactionDescription(tx);
                        const isZeroCost = billingTransactionCostIsZero(tx);
                        return (
                          <li key={tx.id} className="nd1-recruitment-purchases-history-row">
                            <span
                              className={`nd1-recruitment-purchases-history-row__icon-wrap nd1-recruitment-purchases-history-row__icon-wrap--${visualKind}`}
                            >
                              {historyRowIcon(visualKind)}
                            </span>
                            <div className="nd1-recruitment-purchases-history-row__main">
                              <p className="nd1-recruitment-purchases-history-row__title">
                                {billingTransactionTitle(tx)}
                              </p>
                              {description ? (
                                <p className="nd1-recruitment-purchases-history-row__copy">{description}</p>
                              ) : null}
                              <time className="nd1-recruitment-purchases-history-row__when" dateTime={tx.createdAt}>
                                {formatTransactionWhen(tx.createdAt)}
                              </time>
                            </div>
                            <span
                              className={`nd1-recruitment-purchases-history-row__cost${
                                isZeroCost
                                  ? " is-zero"
                                  : costLabel === "Included"
                                    ? " is-included"
                                    : costLabel === "—"
                                      ? ""
                                      : " is-paid"
                              }`}
                            >
                              {costLabel}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <HiringBuyJobsModal
        open={buyModalOpen}
        billingSummary={billingSummary}
        onClose={() => setBuyModalOpen(false)}
      />
      <HiringManagePlanModal open={managePlanOpen} onClose={() => setManagePlanOpen(false)} />
    </RecruitmentShell>
  );
}
